import { lazy, Suspense, useEffect } from '@wordpress/element';
import { Routes, Route, useLocation } from 'react-router-dom';

import { IconProvider } from './components/Icons';

import ScrollToTop from './components/ScrollToTop';
import PlayerProvider from './components/player/PlayerProvider';
import MusicPlayer from './components/player/MusicPlayer';

import GiveawayProvider from './features/giveaways/GiveawayProvider';
import FloatingStickerToken from './features/giveaways/FloatingStickerToken';
import GiveawayLoginModal from './features/giveaways/GiveawayLoginModal';
import GiveawayHubButton from './features/giveaways/GiveawayHubButton';

import Header from './components/Header';
import Footer from './components/Footer';

import MusicHomePage from './pages/MusicHomePage';

const NotFoundPage = lazy(() =>
    import(
        /* webpackChunkName: "not-found" */
        './pages/NotFoundPage'
    )
);

const DiscoverPage = lazy(() =>
    import(
        /* webpackChunkName: "discover" */
        './pages/DiscoverPage'
    )
);

const ChartsPage = lazy(() =>
    import(
        /* webpackChunkName: "charts" */
        './pages/ChartsPage'
    )
);

const ChartPage = lazy(() =>
    import(
        /* webpackChunkName: "chart" */
        './pages/ChartPage'
    )
);

const TrackPage = lazy(() =>
    import(
        /* webpackChunkName: "track" */
        './pages/TrackPage'
    )
);

const UploadPage = lazy(() =>
    import(
        /* webpackChunkName: "upload" */
        './pages/UploadPage'
    )
);

const SearchPage = lazy(() =>
    import(
        /* webpackChunkName: "search" */
        './pages/SearchPage'
    )
);

const GiveawayPage = lazy(() =>
    import(
        /* webpackChunkName: "giveaway" */
        './pages/GiveawayPage'
    )
);

import { trackEvent } from './utils/analytics';

export default function App() {
    const location = useLocation();

    // Dynamically derive a clean title based on the active path structure
    const getPageTitle = (pathname) => {
        if (pathname === '/') return 'Music Home';
        if (pathname === '/upload') return 'Upload Track';
        if (pathname === '/charts') return 'Charts';
        if (pathname.startsWith('/charts/')) return 'Chart Details';
        if (pathname.startsWith('/track/')) return 'Track Details';
        if (pathname === '/giveaway') return 'Giveaway';
        if (pathname.startsWith('/giveaway/')) return 'Giveaway';
        return 'De Crop Over Larder';
    };

    // Automatically trigger a GA4 page view event on every route change
    useEffect(() => {
        trackEvent('page_view', {
            page_title: getPageTitle(location.pathname),
            page_location: window.location.href,
            page_path: location.pathname + location.search
        });
        const params = new URLSearchParams(location.search);

        if (params.get('utm_source') === 'share') {
            trackEvent('share_visit', {
                utm_medium: params.get('utm_medium') || '',
                utm_campaign: params.get('utm_campaign') || '',
                utm_content: params.get('utm_content') || '',
                page_title: getPageTitle(location.pathname),
                page_path: location.pathname + location.search,
            });
        }

    }, [location.pathname, location.search]);


    return (
        <PlayerProvider>
            <GiveawayProvider>
                <ScrollToTop />
                <Header />

                <Suspense fallback={<main className="bjnt-page-loading" aria-live="polite">Loading…</main>}>
                    <Routes>
                        <Route path="/" element={<MusicHomePage />} />
                        <Route path="/upload" element={<UploadPage />} />
                        <Route path="/discover" element={<DiscoverPage />} />
                        <Route path="/charts" element={<ChartsPage />} />
                        <Route path="/charts/:slug" element={<ChartPage />} />
                        <Route path="/track/:slug" element={<TrackPage />} />
                        <Route path="/search" element={<SearchPage />} />
                        <Route path="/giveaway" element={<GiveawayPage />} />
                        <Route path="/giveaway/profile" element={<GiveawayPage />} />
                        <Route path="/giveaway/prizes" element={<GiveawayPage />} />
                        <Route path="/giveaway/schedule" element={<GiveawayPage />} />
                        <Route path="/giveaway/how-to-win" element={<GiveawayPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                </Suspense>

                <Footer />
                <MusicPlayer />
                <FloatingStickerToken />
                <GiveawayHubButton />
                <GiveawayLoginModal />
                <IconProvider />
            </GiveawayProvider>
        </PlayerProvider>
    );
}
